/**
 * ASO-ATTR-001 — the install id: one opaque, stable, per-installation key.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * WHY AN INSTALL ID AT ALL, AND WHY THIS ONE
 *
 * Apple Ads attribution answers a question about an INSTALL ("which campaign produced this copy of
 * the app?"). It arrives before anyone has signed up, so it cannot be keyed to a Firebase UID or an
 * organization; and it must survive the signup that eventually gives it one. That is the whole job
 * of this value, and it is the only job it has.
 *
 * It is NOT a device identifier and must never become one:
 *
 *   * It is generated by us, in JS, and stored in this app's own AsyncStorage. It does not identify
 *     the device to anything else, it is not shared between apps, and it does not survive a
 *     reinstall or a "clear data" — which is the correct behaviour, because a reinstall really is a
 *     new install as far as Apple Ads attribution is concerned.
 *   * We deliberately do NOT use `identifierForVendor` (shared across a vendor's apps, survives
 *     reinstall while any sibling app remains) or the IDFA (needs an ATT prompt, which this feature
 *     exists to avoid). Either would make this a device fingerprint; the value below cannot be one.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * ON THE RANDOMNESS SOURCE — AND WHY IT CHANGED
 *
 * An earlier version of this file said "nothing is authorised by knowing an install id … if that
 * ever stops being true, this function needs a real CSPRNG BEFORE the endpoint changes — not
 * after." That stopped being true inside the same feature: `/attribution/apple-ads/link` now grants
 * an install→organization claim to whoever holds the link secret, and the token endpoint hands that
 * secret to the first submitter of an install id. A predictable id therefore became a predictable
 * capability, and the comment above named the order in which to fix it.
 *
 * So the source is now, in order:
 *
 *   1. `getRandomBytesAsync` from the AdServices native module — `SecRandomCopyBytes`.
 *   2. `crypto.getRandomValues` — every browser, and any RN runtime given a polyfill.
 *   3. `Math.random` — the last resort, on an iOS binary predating the module and on Android.
 *
 * The fallback is not silent: `describeEntropySource()` reports which one produced the id, so
 * "the install ids on this platform are weak" is an observable fact rather than a comment somebody
 * has to find. What remains true is that a guessed id is still only useful within the bounded
 * re-issue window on the server, and never after the install has linked.
 *
 * ⚠️ THE STRONG SOURCE IS NOT PRESENT FOR THE POPULATION THAT WILL ACTUALLY EXIST AT LAUNCH, and an
 * earlier version of this docblock claimed the opposite — that source 1 is "present on exactly the
 * platform where the capability exists". That is false in the order these things ship:
 *
 *   The OTA carrying this JS lands on every already-installed binary, and NONE of them contain
 *   `atlas-adservices`. So `getRandomBytesAsync` returns null, Hermes has no
 *   `crypto.getRandomValues`, and the id is minted from `Math.random`. AsyncStorage survives an App
 *   Store update, so when the 7.1.2 binary arrives those same ids are the ones that submit Apple
 *   tokens and become link-secret-bearing. For iOS that is most of the installed base, not an edge.
 *
 * NOT re-minted when the module later appears, and that is a decision rather than an oversight.
 * Re-keying would break the join this feature exists to produce: `app_first_opened` has already
 * been emitted under the old id, and the whole point of `install_id` on that event is to connect
 * first-opens to attributed installs. Trading a real, fleet-wide measurement loss for a bounded
 * reduction in an already-narrow exposure is the wrong trade — so the exposure is stated here
 * instead, and it is genuinely bounded: re-issue requires the install to be UNLINKED, within
 * `LINK_SECRET_REISSUE_WINDOW_HOURS`, under `MAX_LINK_SECRET_ISSUES`, and the payoff of guessing a
 * specific id is campaign-attribution credit — no money, no customer data, no write to the books.
 *
 * If that trade should be made differently, the lever is to gate the FIRST mint on the native
 * module being available, before any id is persisted — which means Android and web stop getting an
 * install id at all, and `app_first_opened` loses its key on those platforms. That is a product
 * decision, not a cleanup, which is why it is not taken here.
 *
 * ANALYTICS IS NEVER LOAD-BEARING (ATL-901). Every function here resolves to a value; a storage
 * failure yields an ephemeral id and a warning, never a throw into a caller on the launch path.
 */
import AsyncStorage from '@react-native-async-storage/async-storage'

import { getRandomBytesAsync } from '../modules/atlas-adservices'

export const INSTALL_ID_STORAGE_KEY = 'atlas.install.id.v1'

/** RFC 4122 §4.4 shape check. The backend applies the same one, and refuses anything else. */
export const INSTALL_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/

/**
 * Generate a v4 UUID.
 *
 * @returns {string}
 */
/**
 * Whether supplied entropy is actually usable as the id's 16 bytes.
 *
 * ⚠️ ONE PREDICATE, BECAUSE TWO OF THEM DISAGREED. `generateInstallId` has always required
 * `length >= 16`, while the label recording WHICH source produced the id tested the value for mere
 * truthiness — and every typed array is truthy, `new Uint8Array(0)` included. So a native module
 * that returned short or empty bytes produced an id from `Math.random` and reported it as
 * `native`. That is the one direction the label must never fail in: its entire job is to make weak
 * entropy observable, and it would have claimed the STRONGEST source in exactly the case where
 * entropy had silently failed — while that id now keys a capability (`/attribution/apple-ads/link`
 * grants an install→organization claim to whoever holds the secret bound to it).
 *
 * Unreachable today — the Swift returns exactly `count` bytes — so this is a guard, not a bug fix.
 * It is written as a shared predicate rather than a duplicated condition so the two cannot drift
 * apart again, which is how they got here.
 *
 * @param {Uint8Array | null | undefined} entropy
 * @returns {boolean}
 */
export function hasUsableEntropy(entropy) {
  return Boolean(entropy) && entropy.length >= 16
}

export function generateInstallId(entropy) {
  const bytes = new Uint8Array(16)
  if (hasUsableEntropy(entropy)) {
    for (let i = 0; i < 16; i += 1) bytes[i] = entropy[i]
  } else {
    const webCrypto = globalThis.crypto
    if (webCrypto && typeof webCrypto.getRandomValues === 'function') {
      webCrypto.getRandomValues(bytes)
    } else {
      for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256)
    }
  }
  // Version 4, variant 10xx — the two fields INSTALL_ID_PATTERN (and the backend) check.
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80

  const hex = []
  for (let i = 0; i < 16; i += 1) hex.push(bytes[i].toString(16).padStart(2, '0'))
  return (
    `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-` +
    `${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`
  )
}

// One read per JS process. Two callers racing on first launch would otherwise generate two ids and
// persist the loser's — and the loser is the one an already-sent attribution row is keyed by.
let inFlight = null

/**
 * Read the install id, creating and persisting one on first call.
 *
 * @param {{storage?: typeof AsyncStorage, randomBytes?: (n: number) => Promise<Uint8Array|null>}} [deps]
 * @returns {Promise<string>}
 */
export function getInstallId(deps = {}) {
  const storage = deps.storage || AsyncStorage
  const randomBytes = deps.randomBytes || getRandomBytesAsync
  if (!inFlight) {
    inFlight = resolveInstallId(storage, randomBytes).catch((error) => {
      // A storage failure must not deny the app an install id: an ephemeral one still lets this
      // launch report `app_first_opened` and still lets attribution resolve. It simply will not be
      // remembered, which the state machine sees as a fresh install next launch and handles.
      console.warn('[growth] install id could not be persisted; using an ephemeral one', error)
      // The ephemeral id is MEMOIZED, not discarded. Clearing `inFlight` here would hand a
      // DIFFERENT uuid to every later caller in the same process — and the two callers are the
      // attribution POST and the install→organization link, so the attribution row would be keyed
      // by one id and the link by another, and the organization could never be joined to its
      // campaign. One wrong-but-stable id is recoverable; two ids are not.
      const ephemeral = generateInstallId()
      inFlight = Promise.resolve(ephemeral)
      return ephemeral
    })
  }
  return inFlight
}

/** Test seam. Clears the per-process memo so a test can exercise first-launch behaviour twice. */
export function resetInstallIdCacheForTests() {
  inFlight = null
}

/** Which source produced the last generated id. Reported, never guessed at. */
let lastEntropySource = 'none'

/**
 * The entropy source used for the most recent id: `native` | `webcrypto` | `math-random` | `none`.
 *
 * Exists so "install ids on this platform are weak" is observable rather than inferred from a
 * comment. Nothing branches on it.
 *
 * @returns {string}
 */
export function describeEntropySource() {
  return lastEntropySource
}

/**
 * @param {typeof AsyncStorage} storage
 * @param {(count: number) => Promise<Uint8Array | null>} randomBytes
 * @returns {Promise<string>}
 */
async function resolveInstallId(storage, randomBytes) {
  const stored = await storage.getItem(INSTALL_ID_STORAGE_KEY)
  // A value that does not match the shape is treated as absent and REPLACED. Anything else would
  // let a corrupted key propagate to the backend, which refuses it — and then this install could
  // never be attributed, silently, forever.
  if (typeof stored === 'string' && INSTALL_ID_PATTERN.test(stored)) return stored

  let entropy = null
  try {
    entropy = await randomBytes(16)
  } catch (error) {
    // A native module that threw is not a reason to deny the app an id — but it IS a reason to say
    // which source actually produced one, which the fallback below records.
    console.warn('[growth] secure random unavailable; falling back', error)
  }
  // The SAME predicate `generateInstallId` decides with, so the label cannot describe a source the
  // id did not actually come from. See `hasUsableEntropy`.
  lastEntropySource = hasUsableEntropy(entropy)
    ? 'native'
    : globalThis.crypto && typeof globalThis.crypto.getRandomValues === 'function'
      ? 'webcrypto'
      : 'math-random'

  const created = generateInstallId(entropy)
  await storage.setItem(INSTALL_ID_STORAGE_KEY, created)
  return created
}
